import React from "react";

import BulletIndicatorIcon from "@material-ui/icons/FiberManualRecord";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { determineCourseSurface } from "components/Conditions/CourseSurface/course-surface";
import CourseTypeSymbol from "components/CourseTypeSymbol";
import enumParser from "utils/enum-parser";
import { DASH } from "common/constants";
import useStyles from "./styles";

export enum EWorkoutView {
  WORKOUT_REQUEST_VIEW = "workout_request_view",
  HORSE_DETAILS_VIEW = "horse_details_view"
}

interface IProps {
  workout: racehorse360.IWorkout | racehorse360.IHorseWorkout;
  view?: EWorkoutView;
}

const WorkoutDetails = (props: IProps) => {
  const { workout, view = EWorkoutView.HORSE_DETAILS_VIEW } = props;
  const classes = useStyles();

  const isHorseDetailsView = view === EWorkoutView.HORSE_DETAILS_VIEW;
  const isGate = Boolean(workout.gateIndicator);
  const isDog = Boolean(workout.dogIndicator);
  const workoutDistance = Math.round((workout.distance || 0) * 0.01);
  const workoutDistanceUnit = enumParser.parseDistanceUnit(
    workout.distanceUnit
  );
  const workoutTrackCondition = enumParser.parseTrackCondition(
    workout.trackCondition
  );
  const workoutType = enumParser.parseWorkoutType(
    workout.workoutType,
    !isHorseDetailsView
  );
  const workoutTimingReversed = String(workout.timing || 0)
    .split("")
    .reverse();
  const workoutTiming = Array.from(workoutTimingReversed, (char, index) => {
    if (index % 2) {
      return ":" + char;
    } else {
      return char;
    }
  })
    .reverse()
    .join("");

  const shouldShowBulletIndicator = [
    isHorseDetailsView,
    workout.ranking === 1
  ].every(Boolean);
  const shouldShowWorkoutDistance = [workoutDistance, workoutDistanceUnit].some(
    Boolean
  );
  const shouldShowWorkoutType = [workoutType, isGate, isDog].some(Boolean);
  const shouldShowGateIndicator = [isHorseDetailsView, isGate].every(Boolean);
  const shouldShowDogIndicator = [isHorseDetailsView, isDog].every(Boolean);
  const shouldShowWorkoutRanking = [
    workout.ranking,
    workout.numberInRankingGroup
  ].some(Boolean);

  const renderWorkoutCourseSurface = () => {
    let content;

    if (isHorseDetailsView) {
      content = determineCourseSurface(workout, classes);
    }
    if (!isHorseDetailsView && workout.courseSurface) {
      content = (
        <CourseTypeSymbol
          courseType={workout.courseSurface}
          className={classes.courseTypeSymbol}
        />
      );
    }

    return <div className={classes.workoutDetailsDataItem}>{content}</div>;
  };

  return (
    <>
      {shouldShowBulletIndicator && (
        <BulletIndicatorIcon className={classes.bulletIndicator} />
      )}
      {renderWorkoutCourseSurface()}
      {shouldShowWorkoutDistance && (
        <div className={classes.workoutDetailsDataItem}>
          {workoutDistance || DASH}
          {workoutDistanceUnit || DASH}
        </div>
      )}
      {workoutTrackCondition && (
        <div className={classes.workoutDetailsDataItem}>
          {workoutTrackCondition}
        </div>
      )}
      {workoutTiming && (
        <div className={classes.workoutDetailsDataItem}>{workoutTiming}</div>
      )}
      {shouldShowWorkoutType && (
        <div className={classes.workoutDetailsDataItem}>
          {workoutType}
          {shouldShowGateIndicator ? "g" : ""}
          {shouldShowDogIndicator ? "(d)" : ""}
        </div>
      )}
      {shouldShowWorkoutRanking && (
        <div className={classes.workoutDetailsDataItem}>
          {workout.ranking || DASH}/{workout.numberInRankingGroup || DASH}
        </div>
      )}
    </>
  );
};

export default WorkoutDetails;
